import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { t } from '../i18n/index.jsx';
import { useWorkspace, WORKSPACE_FUNDS, WORKSPACE_TRADE } from '../context/WorkspaceContext.jsx';

const STATIC_LINKS = [
  { id: 'nav-dash', group: 'nav', label: 'Dashboard', path: '/', workspace: 'both' },
  { id: 'nav-companies', group: 'nav', label: 'Companies', path: '/companies', workspace: 'both' },
  { id: 'nav-projects', group: 'nav', label: 'Projects', path: '/projects', workspace: WORKSPACE_TRADE },
  { id: 'nav-contracts', group: 'nav', label: 'Contracts', path: '/contracts', workspace: WORKSPACE_TRADE },
  { id: 'nav-exclusivities', group: 'nav', label: 'Exclusivities', path: '/exclusivities', workspace: WORKSPACE_TRADE },
  { id: 'nav-funds', group: 'nav', label: 'Funds', path: '/funds', workspace: WORKSPACE_FUNDS },
  { id: 'nav-tasks', group: 'nav', label: 'Tasks', path: '/tasks', workspace: 'both' },
  { id: 'nav-my-tasks', group: 'nav', label: 'My tasks', path: '/tasks?mine=1', workspace: 'both' },
  { id: 'nav-calendar', group: 'nav', label: 'Calendar', path: '/calendar', workspace: 'both' },
  { id: 'nav-assistant', group: 'nav', label: 'Assistant', path: '/assistant', workspace: 'both' },
];

function groupLabel(g) {
  return ({
    nav: t('Go to'),
    projects: t('Projects'),
    companies: t('Companies'),
    tasks: t('Tasks'),
    funds: t('Funds'),
    contracts: t('Contracts'),
  })[g] || g;
}

export default function CommandPalette() {
  const navigate = useNavigate();
  const { workspace, isFunds, isTrade } = useWorkspace();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [remote, setRemote] = useState(null);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    function onKey(e) {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
      }
    }
    function onOpen() { setOpen(true); }
    window.addEventListener('keydown', onKey);
    window.addEventListener('agevolami:cmdk', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('agevolami:cmdk', onOpen);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQ('');
      setRemote(null);
      setActive(0);
      return;
    }
    const tmr = setTimeout(() => inputRef.current?.focus(), 20);
    return () => clearTimeout(tmr);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const query = q.trim();
    if (query.length < 1) {
      setRemote(null);
      return;
    }
    const id = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(query)}`)
        .then(setRemote)
        .catch(() => setRemote(null));
    }, 180);
    return () => clearTimeout(id);
  }, [q, open]);

  const items = useMemo(() => {
    const query = q.trim().toLowerCase();
    const out = [];
    for (const link of STATIC_LINKS) {
      if (link.workspace !== 'both' && link.workspace !== workspace) continue;
      if (!query || t(link.label).toLowerCase().includes(query) || link.label.toLowerCase().includes(query)) {
        out.push({ ...link, label: t(link.label) });
      }
    }
    if (remote) {
      if (isTrade) {
        (remote.projects || []).forEach((p) => out.push({
          id: `p-${p.id}`, group: 'projects', label: p.name,
          hint: p.country || p.status, path: `/projects/${p.id}`,
        }));
        (remote.contracts || []).forEach((c) => out.push({
          id: `ct-${c.id}`, group: 'contracts', label: c.title,
          hint: c.project_name || c.status, path: '/contracts',
        }));
      }
      if (isFunds) {
        (remote.funds || []).forEach((f) => out.push({
          id: `f-${f.id}`, group: 'funds', label: f.name,
          hint: f.reference_code || f.status, path: `/funds/${f.id}`,
        }));
      }
      // Shared
      (remote.companies || []).forEach((c) => out.push({
        id: `c-${c.id}`, group: 'companies', label: c.name,
        hint: c.type, path: '/companies',
      }));
      (remote.tasks || []).forEach((task) => out.push({
        id: `t-${task.id}`, group: 'tasks', label: task.title,
        hint: task.project_name || task.status, path: `/tasks?view=list`,
      }));
      // Other workspace results (lower priority, still reachable)
      if (isFunds && remote.projects?.length) {
        (remote.projects || []).slice(0, 3).forEach((p) => out.push({
          id: `p-other-${p.id}`, group: 'projects', label: p.name,
          hint: `${t('Trade')} · ${p.country || p.status}`, path: `/projects/${p.id}`,
        }));
      }
      if (isTrade && remote.funds?.length) {
        (remote.funds || []).slice(0, 3).forEach((f) => out.push({
          id: `f-other-${f.id}`, group: 'funds', label: f.name,
          hint: `${t('Funds')} · ${f.reference_code || f.status}`, path: `/funds/${f.id}`,
        }));
      }
    }
    return out.slice(0, 40);
  }, [q, remote, workspace, isFunds, isTrade]);

  useEffect(() => { setActive(0); }, [items.length, q]);

  const go = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    navigate(item.path);
  }, [navigate]);

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, Math.max(0, items.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(items[active]);
    }
  }

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  let lastGroup = null;

  return createPortal(
    <div className="cmdk-backdrop" onMouseDown={() => setOpen(false)}>
      <div className="cmdk" role="dialog" aria-modal="true" aria-label={t('Command palette')} onMouseDown={(e) => e.stopPropagation()}>
        <div className="cmdk-input-row">
          <span className="cmdk-icon" aria-hidden>⌘</span>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t('Search pages, projects, tasks…')}
            autoComplete="off"
          />
          <kbd className="cmdk-kbd">esc</kbd>
        </div>
        <div className="cmdk-list" ref={listRef}>
          {items.length === 0 ? (
            <div className="cmdk-empty muted">{t('No results')}</div>
          ) : (
            items.map((item, idx) => {
              const showGroup = item.group !== lastGroup;
              lastGroup = item.group;
              return (
                <div key={item.id}>
                  {showGroup && <div className="cmdk-group">{groupLabel(item.group)}</div>}
                  <button
                    type="button"
                    data-idx={idx}
                    className={`cmdk-item ${idx === active ? 'active' : ''}`}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => go(item)}
                  >
                    <span className="cmdk-item-label">{item.label}</span>
                    {item.hint && <span className="cmdk-item-hint muted">{item.hint}</span>}
                  </button>
                </div>
              );
            })
          )}
        </div>
        <div className="cmdk-foot muted sm">
          <span>↑↓ {t('navigate')}</span>
          <span>↵ {t('open')}</span>
          <span>esc {t('close')}</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
